/**
 * Color Utilities
 * Generic utilities for color handling and synchronization
 * Used by: financial-metrics, detailed_view dashboards
 */

// ============ TYPES ============

export interface ColorConfig {
  colorMap?: Record<string, string>;
  topNColorsByStackPosition?: string[];
  colorSyncEnabled?: boolean;
}

// ============ COLOR RETRIEVAL UTILITIES ============

/**
 * Get color for a series by index and name
 * Priority: position-based colors first, then name-based colorMap
 */
export function getSeriesColor(
  index: number,
  seriesName: string,
  config: ColorConfig
): string | null {
  const { colorMap = {}, topNColorsByStackPosition: positionColors = [] } = config;

  // Position-based color (for cross-chart color consistency)
  if (positionColors.length > 0 && index < positionColors.length) {
    return positionColors[index];
  }

  // Name-based color from colorMap
  if (colorMap[seriesName]) {
    return colorMap[seriesName];
  }

  return null;
}

/**
 * Get colors for multiple series indices
 */
export function getColorsForIndices(
  indices: number[],
  seriesNames: string[],
  config: ColorConfig
): string[] {
  return indices
    .map((index) => {
      const seriesName = seriesNames[index];
      return getSeriesColor(index, seriesName, config);
    })
    .filter((color): color is string => color !== null);
}

/**
 * Build color map from series data
 */
export function buildColorMap(
  seriesNames: string[],
  colors: string[]
): Record<string, string> {
  const colorMap: Record<string, string> = {};

  seriesNames.forEach((name, index) => {
    if (index < colors.length) {
      colorMap[name] = colors[index];
    }
  });

  return colorMap;
}

// ============ COLOR MATCHING UTILITIES ============

/**
 * Normalize color for comparison (uppercase, trim)
 */
export function normalizeColor(color: string): string {
  return color?.toUpperCase().trim() || '';
}

/**
 * Check if two colors match (case-insensitive)
 */
export function colorsMatch(color1: string, color2: string): boolean {
  return normalizeColor(color1) === normalizeColor(color2);
}

/**
 * Find series indices that match given colors
 */
export function findIndicesByColors(
  targetColors: string[],
  seriesNames: string[],
  config: ColorConfig
): number[] {
  const normalizedTargetColors = targetColors.map(normalizeColor);
  const matchingIndices: number[] = [];

  seriesNames.forEach((seriesName, index) => {
    const seriesColor = getSeriesColor(index, seriesName, config);

    if (seriesColor && normalizedTargetColors.includes(normalizeColor(seriesColor))) {
      matchingIndices.push(index);
    }
  });

  return matchingIndices;
}

/**
 * Check if any target colors match series colors
 */
export function hasMatchingColors(
  targetColors: string[],
  seriesNames: string[],
  config: ColorConfig
): boolean {
  return findIndicesByColors(targetColors, seriesNames, config).length > 0;
}

// ============ DEFAULT COLOR PALETTES ============

/**
 * Default color palette for charts
 */
export const DEFAULT_CHART_COLORS = [
  '#5470C6', // Blue
  '#91CC75', // Green
  '#FAC858', // Yellow
  '#EE6666', // Red
  '#73C0DE', // Cyan
  '#3BA272', // Dark Green
  '#FC8452', // Orange
  '#9A60B4', // Purple
  '#EA7CCC', // Pink
];

/**
 * Extended color palette for more series
 */
export const EXTENDED_CHART_COLORS = [
  '#5470C6', '#91CC75', '#FAC858', '#EE6666', '#73C0DE',
  '#3BA272', '#FC8452', '#9A60B4', '#EA7CCC', '#4ECDC4',
  '#FF6B6B', '#C7F464', '#4D4D4D', '#81D4FA', '#AED581',
  '#FFD54F', '#FF8A65', '#BA68C8', '#4DB6AC', '#7986CB',
];

/**
 * Get color from default palette by index
 */
export function getDefaultColor(index: number): string {
  return EXTENDED_CHART_COLORS[index % EXTENDED_CHART_COLORS.length];
}

/**
 * Generate colors for N series using default palette
 */
export function generateColors(count: number): string[] {
  return Array.from({ length: count }, (_, i) => getDefaultColor(i));
}

// ============ COLOR MANIPULATION UTILITIES ============

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Lighten a hex color by percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  const newR = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor));
  const newG = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor));
  const newB = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor));

  return rgbToHex(newR, newG, newB);
}

/**
 * Darken a hex color by percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = 1 - percent / 100;
  const newR = Math.round(rgb.r * factor);
  const newG = Math.round(rgb.g * factor);
  const newB = Math.round(rgb.b * factor);

  return rgbToHex(newR, newG, newB);
}

/**
 * Add alpha (opacity) to hex color
 */
export function addAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Check if color is light or dark
 */
export function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;

  // Using relative luminance formula
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
}

/**
 * Get contrasting text color (black or white) for a background
 */
export function getContrastingTextColor(backgroundColor: string): string {
  return isLightColor(backgroundColor) ? '#333333' : '#FFFFFF';
}

// ============ COLOR SYNC UTILITIES ============

/**
 * Sync color selection between charts
 */
export function syncColorSelection(
  sourceColors: string[],
  targetSeriesNames: string[],
  targetConfig: ColorConfig
): number[] {
  return findIndicesByColors(sourceColors, targetSeriesNames, targetConfig);
}

/**
 * Build color sync data for broadcasting
 */
export function buildColorSyncData(
  selectedIndices: number[],
  seriesNames: string[],
  config: ColorConfig
): {
  selectedColors: string[];
  colorSyncEnabled: boolean;
} {
  const selectedColors = getColorsForIndices(selectedIndices, seriesNames, config);
  const colorSyncEnabled = config.colorSyncEnabled === true;

  return {
    selectedColors,
    colorSyncEnabled,
  };
}